import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle, Eye, EyeOff, Lock, LogOut, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'

import ProfileService from '../../services/profile'
import { useAuth } from '../../contexts/AuthContext'
import { calculatePasswordStrength, isPasswordValid } from '../../utils/passwordStrength'
import { Alert } from '../ui/Alert'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { MODAL_Z_INDEX } from '../ui/Modal'

/**
 * CHANGEMENT DE MOT DE PASSE FORCÉ (LOT-MODAL-MDP).
 *
 * Monté par `App.tsx` quand `user.mustChangePassword` est vrai. Il recouvre
 * TOUTE l'application : tant que le mot de passe n'est pas changé, rien
 * d'autre n'est accessible.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE N'EST PAS LE `Modal` DU KIT
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Le `Modal` de H7 rend une croix SYSTÉMATIQUE (invariant DA-018) et ferme
 * sur Échap et clic-overlay — trois sorties que cette surface ne doit
 * précisément PAS offrir : elle est bloquante par contrat produit. Plutôt que
 * d'ajouter au kit un mode « sans issue » qui contredirait son invariant, ce
 * fichier garde un conteneur dédié, mais construit avec les tokens et les
 * primitives du kit (`Input`, `Button`, `Alert`, `MODAL_Z_INDEX`) : pas de
 * croix, pas d'Échap, pas de fermeture au clic sur le voile.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ON NE MURE JAMAIS PERSONNE — L'INCIDENT FONDATEUR
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Un utilisateur réel s'est retrouvé enfermé : session restaurée d'un compte
 * à `mustChangePassword=true` dont il ne connaissait PAS le mot de passe
 * temporaire — et l'ancien modal recouvrait tout sans aucune issue. Règle
 * produit : la seule issue sans changer le mot de passe est le bouton
 * « Se déconnecter », qui appelle le `logout` de l'`AuthContext` et ramène à
 * l'écran de connexion (changement de compte possible).
 *
 * Deuxième volet du même incident : Chrome REMPLISSAIT le champ « mot de
 * passe actuel » avec l'ancien mot de passe enregistré, à l'insu de
 * l'utilisateur — d'où `autoComplete="off"` sur le champ temporaire et
 * `autoComplete="new-password"` sur les deux autres, et des libellés honnêtes
 * (« Mot de passe temporaire », pas « actuel »).
 *
 * Les erreurs de soumission (401 compris) s'affichent en `Alert` danger DANS
 * le modal — un toast se rate, une alerte dans le flux non.
 */

interface ForcePasswordChangeModalProps {
  onSuccess: () => void
}

/**
 * Bascule « afficher / masquer » posée DANS le champ — la recette de
 * `SecurityTab` (LOT-H6) : `size="sm"` pour tenir dans les 42 px du champ, la
 * cible tactile de 44 px est rétablie par le pseudo-élément du `Button`.
 */
const RevealButton = ({
  shown,
  onToggle,
  label,
}: {
  shown: boolean
  onToggle: () => void
  label: string
}) => (
  <Button
    variant="ghost"
    size="sm"
    icon={shown ? EyeOff : Eye}
    onClick={onToggle}
    aria-label={label}
    aria-pressed={shown}
  />
)

/** Éléments focusables — même sélecteur que le `Modal` du kit, pour le piège de focus. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const ForcePasswordChangeModal = ({ onSuccess }: ForcePasswordChangeModalProps) => {
  const { t } = useTranslation('common')
  const { logout } = useAuth()
  const dialogRef = useRef<HTMLDivElement>(null)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  /** Erreur de la DERNIÈRE soumission, rendue en `Alert` danger dans le flux (M-3). */
  const [submitError, setSubmitError] = useState<string | null>(null)

  const passwordStrength = calculatePasswordStrength(newPassword)
  const isValid = isPasswordValid(newPassword)
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0

  /**
   * PIÈGE DE FOCUS — Tab et Maj+Tab bouclent dans la boîte (même recette que
   * le `Modal` du kit). PAS d'écouteur Échap : cette surface ne se ferme pas.
   */
  useEffect(() => {
    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const dialog = dialogRef.current
      if (!dialog) return

      const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (focusables.length === 0) return

      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement as HTMLElement | null

      if (!active || !dialog.contains(active)) {
        event.preventDefault()
        first.focus()
        return
      }
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
        return
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleTab, true)
    return () => window.removeEventListener('keydown', handleTab, true)
  }, [])

  /** Le fond ne défile pas sous le voile (chaînage de défilement iPad). */
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isValid) {
      setSubmitError(t('password.tooShort'))
      return
    }

    if (!passwordsMatch) {
      setSubmitError(t('password.force.mismatch'))
      return
    }

    try {
      setIsSubmitting(true)
      setSubmitError(null)
      await ProfileService.changePassword({
        currentPassword,
        newPassword,
      })
      toast.success(t('password.force.success'))
      onSuccess()
    } catch (error: any) {
      console.error('Password change error:', error)
      if (error.response?.status === 401) {
        setSubmitError(t('password.force.errors.temporaryIncorrect'))
      } else {
        setSubmitError(error.response?.data?.message || t('password.force.errors.generic'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return createPortal(
    <div
      role="presentation"
      data-testid="force-password-overlay"
      /* Voile SANS fermeture au clic — surface bloquante. `touchAction: 'none'` :
         un glissement sur le voile ne fait pas défiler la page derrière. */
      style={{ touchAction: 'none', zIndex: MODAL_Z_INDEX }}
      className="fixed inset-0 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="force-password-title"
        aria-describedby="force-password-subtitle"
        className="flex max-h-[90vh] w-full max-w-[560px] flex-col overflow-hidden rounded-card border border-border bg-background-primary shadow-2xl"
      >
        {/* En-tête : tuile neutre + titre T2, PAS de croix (surface bloquante). */}
        <div className="flex shrink-0 items-center gap-3 border-b border-border px-5 py-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-background-secondary text-foreground-secondary">
            <Lock className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 id="force-password-title" className="truncate text-t2 text-foreground">
              {t('password.force.title')}
            </h2>
            <p id="force-password-subtitle" className="mt-0.5 truncate text-t5 text-foreground-secondary">
              {t('password.force.subtitle')}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-5 py-5">
            {/* Contexte honnête : compte créé OU réinitialisé (M-2). */}
            <Alert tone="warning" title={t('password.force.context')} />

            {/* L'erreur de soumission vit DANS le flux du modal (M-3). */}
            {submitError && <Alert tone="danger" title={submitError} />}

            {/* Mot de passe temporaire — `autoComplete="off"` : Chrome collait
                l'ancien mot de passe enregistré dans ce champ à l'insu de
                l'utilisateur (l'incident réel). */}
            <Input
              type={showCurrentPassword ? 'text' : 'password'}
              name="temporary-password"
              autoComplete="off"
              label={t('password.force.temporaryLabel')}
              hint={t('password.force.temporaryHint')}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoFocus
              trailing={
                <RevealButton
                  shown={showCurrentPassword}
                  onToggle={() => setShowCurrentPassword(!showCurrentPassword)}
                  label={showCurrentPassword ? t('password.hide') : t('password.reveal')}
                />
              }
            />

            {/* Nouveau mot de passe */}
            <div>
              <Input
                type={showNewPassword ? 'text' : 'password'}
                name="new-password"
                autoComplete="new-password"
                label={t('password.force.newLabel')}
                hint={t('password.policy')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                trailing={
                  <RevealButton
                    shown={showNewPassword}
                    onToggle={() => setShowNewPassword(!showNewPassword)}
                    label={showNewPassword ? t('password.hide') : t('password.reveal')}
                  />
                }
              />

              {/* Jauge de force — indicative (LOT-MDP-8), même dessin que SecurityTab. */}
              {newPassword && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-background-secondary">
                    <div
                      className="h-full transition-all duration-300"
                      style={{
                        width: `${passwordStrength.percentage}%`,
                        backgroundColor: passwordStrength.color,
                      }}
                    />
                  </div>
                  <span className="text-t5 font-semibold" style={{ color: passwordStrength.color }}>
                    {passwordStrength.label}
                  </span>
                </div>
              )}
            </div>

            {/* Confirmation */}
            <div>
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirm-password"
                autoComplete="new-password"
                label={t('password.force.confirmLabel')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                trailing={
                  <RevealButton
                    shown={showConfirmPassword}
                    onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                    label={showConfirmPassword ? t('password.hide') : t('password.reveal')}
                  />
                }
              />
              {confirmPassword && (
                <div
                  className={`mt-2 flex items-center gap-2 text-t5 ${
                    passwordsMatch ? 'text-success-400' : 'text-danger-400'
                  }`}
                >
                  {passwordsMatch ? (
                    <>
                      <CheckCircle size={16} aria-hidden="true" />
                      <span>{t('password.force.match')}</span>
                    </>
                  ) : (
                    <>
                      <XCircle size={16} aria-hidden="true" />
                      <span>{t('password.force.mismatch')}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Pied TOUJOURS visible. La déconnexion est la SEULE issue sans
              changer le mot de passe (M-1) : elle appelle le logout de
              l'AuthContext tel quel — retour à l'écran de connexion. */}
          <div className="shrink-0 border-t border-border px-5 py-4">
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                type="button"
                icon={LogOut}
                onClick={logout}
                className="flex-1"
              >
                {t('password.force.logout')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                icon={Lock}
                loading={isSubmitting}
                disabled={isSubmitting || !isValid || !passwordsMatch || !currentPassword}
                className="flex-1"
              >
                {t('password.force.submit')}
              </Button>
            </div>
            <p className="mt-2 text-center text-t5 text-foreground-tertiary">
              {t('password.force.logoutHint')}
            </p>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

export default ForcePasswordChangeModal
